"use client";

import { useEffect, useState } from "react";
import { usePrivy } from "@privy-io/react-auth";

const API_BASE_URL = process.env.NEXT_PUBLIC_API_URL ?? "http://localhost:3000";

type SalespersonOption = {
  id: number;
  email: string;
  walletAddress: string;
  isAdministrator: boolean;
};

export default function ChangeSalespersonButton({ agreementId }: { agreementId: number }) {
  const { getAccessToken } = usePrivy();
  const [open, setOpen] = useState(false);
  const [wallet, setWallet] = useState("");
  const [options, setOptions] = useState<SalespersonOption[]>([]);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open || options.length > 0) return;

    (async () => {
      try {
        const token = await getAccessToken();
        if (!token) return;
        const response = await fetch(`${API_BASE_URL}/api/agreements/account-salespersons`, {
          cache: "no-store",
          headers: { Authorization: `Bearer ${token}` },
        });
        const payload = await response.json();
        if (response.ok && payload.success) setOptions(payload.salespersons);
      } catch {
        setError("Unable to load the salesperson list.");
      }
    })();
  }, [open, options.length, getAccessToken]);

  async function submit() {
    setBusy(true);
    setError(null);
    setMessage(null);
    try {
      const token = await getAccessToken();
      if (!token) throw new Error("Sign in again to continue.");
      const response = await fetch(`${API_BASE_URL}/api/agreements/${agreementId}/account-salesperson`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ salespersonWallet: wallet.trim() }),
      });
      const payload = await response.json();
      if (!response.ok || !payload.success) throw new Error(payload.error || "Unable to change the salesperson.");
      setMessage("Salesperson updated. Reload to see the change.");
      setOpen(false);
      setWallet("");
    } catch (submitError) {
      setError(submitError instanceof Error ? submitError.message : "Unable to change the salesperson.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="w-full">
      {!open ? (
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="rounded-lg border border-blue-500/40 bg-blue-500/10 px-3 py-1.5 text-xs text-blue-200 hover:border-blue-400"
        >
          Change Salesperson
        </button>
      ) : (
        <div className="space-y-2">
          <select
            value={wallet}
            onChange={(event) => setWallet(event.target.value)}
            className="w-full rounded-lg border border-slate-700 bg-slate-950 px-3 py-1.5 text-xs text-white"
          >
            <option value="">Select a salesperson...</option>
            {options.map((option) => (
              <option key={option.id} value={option.walletAddress}>
                {option.email || option.walletAddress}
                {option.isAdministrator ? " (Administrator, default)" : ""}
              </option>
            ))}
          </select>
          <div className="flex gap-2">
            <button
              type="button"
              disabled={busy || !wallet.trim()}
              onClick={submit}
              className="rounded-lg bg-blue-600 px-3 py-1.5 text-xs font-semibold text-white disabled:opacity-50"
            >
              {busy ? "Submitting on-chain..." : "Confirm"}
            </button>
            <button
              type="button"
              disabled={busy}
              onClick={() => setOpen(false)}
              className="rounded-lg border border-slate-600 px-3 py-1.5 text-xs text-slate-200"
            >
              Cancel
            </button>
          </div>
        </div>
      )}
      {message && <p className="mt-2 text-xs text-emerald-300">{message}</p>}
      {error && <p className="mt-2 text-xs text-red-300">{error}</p>}
    </div>
  );
}
